// Anant Smart Panel — Unified Admin Command Center
// Aggregates all pending requests (deposits, orders, support, AT deposits, tool activations, renewals, delivery partners)
// Sound + voice alert toggles for new items via polling delta.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, BellOff, Volume2, VolumeX, RefreshCw, CheckCircle2, XCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const KIND_META = {
  fund_deposit:       { label: '💰 Fund Deposits',   color: 'bg-emerald-500' },
  at_deposit:         { label: '🪙 AT Deposits',     color: 'bg-yellow-500' },
  marketplace_order:  { label: '📦 Orders',          color: 'bg-purple-500' },
  support:            { label: '🆘 Support',         color: 'bg-rose-500' },
  delivery_partner:   { label: '🛵 Delivery',        color: 'bg-cyan-500' },
  tool_activation:    { label: '🔓 Tool Activations', color: 'bg-indigo-500' },
  renewal:            { label: '🔁 Renewals',        color: 'bg-amber-500' },
};

function playBeep(customUrl) {
  // If admin set a custom alert tone, play that audio file
  if (customUrl) {
    try {
      const audio = new Audio(customUrl.startsWith('http') ? customUrl : `${process.env.REACT_APP_BACKEND_URL || ''}${customUrl}`);
      audio.volume = 0.7;
      audio.play().catch(() => {});
      return;
    } catch {}
  }
  // Fallback: pleasant 2-note synthesized chime (0-cost)
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    [880, 1108].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = freq;
      o.connect(g); g.connect(ctx.destination);
      const start = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.001, start);
      g.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.5);
      o.start(start); o.stop(start + 0.52);
    });
  } catch {}
}

function speak(text) {
  try {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-IN'; u.rate = 1.05; u.pitch = 1.0; u.volume = 0.9;
    window.speechSynthesis.speak(u);
  } catch {}
}

export default function AdminSmartPanel() {
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('smartpanel_sound') !== 'off');
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('smartpanel_voice') !== 'off');
  const [customTone, setCustomTone] = useState(null);
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  useEffect(() => {
    api.get('/me/tool-visibility').then((r) => setCustomTone(r.data.alert_tone_url || null)).catch(() => {});
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/admin/smart-panel/feed', { params: kindFilter === 'all' ? {} : { kind: kindFilter } });
      const arr = r.data.items || [];
      setCounts(r.data.counts_by_kind || {});
      // Detect new items after first load → play alerts
      if (!firstLoad.current) {
        const newOnes = arr.filter((it) => !seenIds.current.has(`${it.kind}:${it.id}`));
        if (newOnes.length > 0) {
          if (soundOn) playBeep(customTone);
          if (voiceOn) {
            const n = newOnes[0];
            speak(`New ${n.kind.replace(/_/g, ' ')} request: ${n.title.replace(/[^a-zA-Z0-9 .]/g, '')}`);
          }
          toast.success(`🔔 ${newOnes.length} new request${newOnes.length > 1 ? 's' : ''}`);
        }
      }
      seenIds.current = new Set(arr.map((it) => `${it.kind}:${it.id}`));
      firstLoad.current = false;
      setItems(arr);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { if (!silent) setLoading(false); }
  }, [kindFilter, soundOn, voiceOn, customTone]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 10000);
    return () => clearInterval(t);
  }, [load]);

  const toggleSound = () => { const nv = !soundOn; setSoundOn(nv); localStorage.setItem('smartpanel_sound', nv ? 'on' : 'off'); if (nv) playBeep(customTone); };
  const toggleVoice = () => { const nv = !voiceOn; setVoiceOn(nv); localStorage.setItem('smartpanel_voice', nv ? 'on' : 'off'); if (nv) speak('Voice alerts on'); };

  const act = async (item, action) => {
    let reason = null;
    let assign_to = null;
    if (action === 'reject') {
      reason = window.prompt(`Reject "${item.title}"?\n\nReason (shown to user):`, '');
      if (reason === null) return;
      if (!reason.trim()) { toast.error('Reason required'); return; }
    } else if (action === 'assign') {
      assign_to = window.prompt('Assign to (partner_id or agent_id):', '');
      if (!assign_to) return;
    } else {
      if (!window.confirm(`${action.toUpperCase()}: ${item.title}?`)) return;
    }
    try {
      await api.post('/admin/smart-panel/action', { kind: item.kind, id: item.id, action, reason, assign_to });
      toast.success(`✅ ${action.toUpperCase()} ${item.kind}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const chips = useMemo(() => [
    { key: 'all', label: `All (${Object.values(counts).reduce((a, b) => a + b, 0)})` },
    ...Object.entries(KIND_META).map(([k, m]) => ({ key: k, label: `${m.label} (${counts[k] || 0})` })),
  ], [counts]);

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="admin-smart-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-black">🧠 Anant Smart Panel</h1>
            <p className="text-xs opacity-70">Unified command center · Poll every 10s · Click Approve/Reject inline</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleSound} data-testid="smartpanel-toggle-sound"
                    className={`px-3 py-1.5 rounded-lg text-xs font-black flex items-center gap-1 ${soundOn ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
              {soundOn ? <Bell size={12} /> : <BellOff size={12} />} Sound
            </button>
            <button onClick={toggleVoice} data-testid="smartpanel-toggle-voice"
                    className={`px-3 py-1.5 rounded-lg text-xs font-black flex items-center gap-1 ${voiceOn ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
              {voiceOn ? <Volume2 size={12} /> : <VolumeX size={12} />} Voice
            </button>
            <button onClick={() => load()} data-testid="smartpanel-refresh"
                    className="px-3 py-1.5 rounded-lg bg-slate-700 text-xs font-black flex items-center gap-1">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <button key={c.key} onClick={() => setKindFilter(c.key)} data-testid={`smartpanel-chip-${c.key}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-black transition ${
                      kindFilter === c.key ? 'bg-white text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}>{c.label}</button>
          ))}
        </div>

        {items.length === 0 && !loading && (
          <div className="text-center py-10 opacity-60 text-sm bg-slate-900/40 rounded-2xl border border-slate-800">
            🎉 Sab clear! No pending requests.
          </div>
        )}

        <div className="space-y-2">
          {items.map((it) => {
            const meta = KIND_META[it.kind] || { label: it.kind, color: 'bg-slate-600' };
            const urgBadge = { high: 'bg-rose-500', medium: 'bg-amber-500', low: 'bg-slate-600' }[it.urgency];
            return (
              <div key={`${it.kind}:${it.id}`} data-testid={`smartpanel-item-${it.kind}-${(it.id || '').slice(0,8)}`}
                   className="rounded-xl bg-slate-900/70 border border-slate-800 p-3 flex items-center gap-3">
                <span className={`w-2 h-10 rounded-full ${meta.color}`}></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${urgBadge} text-white`}>{it.urgency}</span>
                    <span className="text-xs font-black truncate">{it.title}</span>
                    {it.amount != null && <span className="text-[10px] text-yellow-300 font-black">₹{Number(it.amount).toLocaleString()}</span>}
                  </div>
                  <div className="text-[10px] opacity-70 truncate">{it.subtitle}</div>
                  <div className="text-[9px] opacity-50">{new Date(it.created_at).toLocaleString('en-IN')}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => act(it, 'approve')} data-testid={`smartpanel-approve-${(it.id || '').slice(0,8)}`}
                          className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center gap-1">
                    <CheckCircle2 size={12} /> Approve
                  </button>
                  <button onClick={() => act(it, 'reject')} data-testid={`smartpanel-reject-${(it.id || '').slice(0,8)}`}
                          className="px-2.5 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-black flex items-center gap-1">
                    <XCircle size={12} /> Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}
